import { useCallback, useRef } from "react";
import {
  useLocation,
  useNavigate,
  type Location,
  type NavigateOptions,
  type To,
} from "react-router-dom";
import { useActivityNavigator } from "../navigation/useActivityNavigator";
import { getActivityRouteRule } from "../navigation/activityRoutes";
import { useActivityStore } from "../store/activityStore";

export interface OverlayNavigationState {
  presentation?: "overlay";
  overlayFallbackPath?: string;
  overlayRootPath?: string;
  overlayStackId?: string;
  overlayDepth?: number;
}

export function useOverlayNavigate() {
  const navigate = useNavigate();
  const location = useLocation();
  const activityNav = useActivityNavigator();

  // location 은 매 navigation 마다 새 객체. 이 값을 useCallback deps 에 넣으면
  // overlayNavigate 의 reference 가 매번 바뀌어, 이를 deps 에 둔 consumer 의
  // useEffect 가 navigation 직후에 한 번 더 실행되는 race condition 을 만든다.
  // (예: /payment/method → /payment/done 직후 큐 검증 effect 가 재실행되며
  //  비워진 큐를 보고 /shop/:id 로 강제 redirect → "결제완료 페이지가 안 뜸").
  // ref 로 최신 location 을 추적하면 callback 은 stable 하게 유지하면서
  // 호출 시점의 최신 state/pathname 을 안전하게 읽을 수 있다.
  const locationRef = useRef<Location>(location);
  locationRef.current = location;

  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === "number") {
        navigate(to);
        return;
      }

      const current = locationRef.current;
      const toUrl = typeof to === "string" ? to : `${to.pathname ?? ""}${to.search ?? ""}${to.hash ?? ""}`;
      const activityRule = getActivityRouteRule(toUrl, current.pathname);
      if (activityRule) {
        const state = options?.state;
        if (options?.replace) {
          const stack = useActivityStore.getState().activities;
          const replacing = stack[stack.length - 1];
          // 이미 아래에 열려 있는 **그 화면**으로 되돌아가는 replace 인가 (예: 문의 삭제 →
          // 고객센터). URL 까지 같아야 "복귀"로 본다.
          const returningTo = [...stack]
            .reverse()
            .find(
              (a) =>
                a.kind === activityRule.kind &&
                a.entries[a.entries.length - 1]?.url === toUrl,
            );
          if (!replacing) {
            activityNav.open({ ...activityRule, state });
          } else if (replacing.kind === activityRule.kind) {
            activityNav.replaceEntry({ url: toUrl, state });
          } else if (returningTo && returningTo !== replacing) {
            // 위에 쌓인 activity 들을 닫고 그리로 내려간다. 새로 쌓으면(replaceActivity)
            // 같은 화면이 두 겹이 되고, history 를 되감지 않으면 뒤로가기가 한 번 헛돈다 —
            // closeFunnel 이 닫는 activity 들의 history 항목까지 pop 한다.
            const aboveKinds = new Set(
              stack.slice(stack.indexOf(returningTo) + 1).map((a) => a.kind),
            );
            activityNav.closeFunnel(aboveKinds, toUrl, { state });
          } else {
            // ⚠ 종류가 다른데 entry 만 갈아끼우면 kind 는 그대로 남아 ActivityHost resolver
            // 가 새 URL 을 못 알아보고 빈 화면이 된다 (세부 버전 선택 → 카드 상세 replace).
            activityNav.replaceActivity({ ...activityRule, url: toUrl, state });
          }
          return;
        }

        const top = useActivityStore.getState().activities.slice(-1)[0];
        if (top && top.kind === activityRule.kind) {
          activityNav.push({ url: toUrl, state });
        } else {
          activityNav.open({ ...activityRule, state });
        }
        return;
      }

      const currentState = current.state as OverlayNavigationState | null;
      const baseState = getOverlayState(currentState, current.pathname);
      const isReplace = options?.replace === true;
      navigate(to, {
        ...options,
        state: {
          ...baseState,
          overlayDepth: isReplace
            ? baseState.overlayDepth ?? 0
            : (baseState.overlayDepth ?? 0) + 1,
          ...(options?.state as object | undefined),
        },
      });
    },
    [navigate, activityNav],
  );
}

function getOverlayState(
  currentState: OverlayNavigationState | null,
  currentPathname: string,
): OverlayNavigationState {
  const rootPath = currentState?.overlayRootPath ?? currentPathname;

  return {
    presentation: "overlay",
    overlayFallbackPath: currentState?.overlayFallbackPath ?? getFallbackPath(currentPathname),
    overlayRootPath: rootPath,
    overlayStackId:
      currentState?.overlayStackId ??
      getDefaultStackId(currentPathname, rootPath),
    overlayDepth: currentState?.overlayDepth ?? 0,
  };
}

function getFallbackPath(pathname: string) {
  if (pathname === "/me" || pathname.startsWith("/me/")) return "/me";
  if (pathname === "/notifications" || pathname.startsWith("/notifications/")) {
    return "/notifications";
  }
  if (pathname === "/support" || pathname.startsWith("/support/")) return "/support";
  return "/";
}

function getDefaultStackId(_pathname: string, rootPath: string) {
  return `/overlay${rootPath}`;
}
